import { useEffect, useRef, useState } from 'react';
import styles from './CameraModal.module.scss';
import { useTranslation } from 'react-i18next';
import { Modal } from 'components/atoms/Modal';

interface CameraModalProps {
  disabled?: boolean;
  onClose: () => void;
  onCapture: (file: File) => void;
  imagePreviewList?: React.ReactChild;
}

export const CameraModal = ({
  disabled = false,
  onClose,
  onCapture,
  imagePreviewList,
}: CameraModalProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
  }, []);

  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }

      setIsVideoReady(false);
      setErrorMessage('');

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setErrorMessage(
          t('common_camera.permission_denied') ||
            'Cannot access camera. Please allow camera permission.'
        );
        return;
      }

      const constraints = {
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(
        constraints
      );
      streamRef.current = mediaStream;

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;

        videoRef.current.onloadedmetadata = () => {
          setIsVideoReady(true);
        };
      }
    } catch (error) {
      setErrorMessage(
        t('common_camera.permission_denied') ||
          'Cannot access camera. Please allow camera permission.'
      );
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsVideoReady(false);
    setErrorMessage('');
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!isVideoReady || video.videoWidth === 0 || video.videoHeight === 0) {
      return;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }

    // This logic ensures the captured photo matches exactly what is visible
    // on screen. The video uses object-fit: cover, which scales the video to
    // fill the preview frame and clips the peripheral parts. Without this,
    // the full raw video frame (including the hidden areas) would be captured.
    // The calculation below computes the visible crop region of the raw frame
    // so that only what the user sees in the preview is saved as the photo.
    // Actual video resolution
    const vw = video.videoWidth;
    const vh = video.videoHeight;

    // Displayed size of the video element on screen (the preview frame)
    const displayWidth = video.clientWidth;
    const displayHeight = video.clientHeight;

    // object-fit: cover scales the video so it fully covers the container,
    // clipping the excess. Calculate the scale factor and the visible crop region.
    const scale = Math.max(displayWidth / vw, displayHeight / vh);
    const visibleWidth = displayWidth / scale;
    const visibleHeight = displayHeight / scale;
    const sx = (vw - visibleWidth) / 2;
    const sy = (vh - visibleHeight) / 2;

    // Output canvas matches the visible region's natural pixel size
    canvas.width = Math.round(visibleWidth);
    canvas.height = Math.round(visibleHeight);

    context.drawImage(
      video,
      sx,
      sy,
      visibleWidth,
      visibleHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `photo_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          onCapture(file);
        }
      },
      'image/jpeg',
      0.95
    );
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className={styles.cameraModal}>
      <Modal contentClassName={styles.cameraModalContent}>
        <div className={styles.header}>
          <div className={styles.closeButton} onClick={handleClose}>
            {t('common.return')}
          </div>
        </div>

        <div className={styles.body}>
          <div className={styles.cameraArea}>
            <div className={styles.cameraContainer}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className={styles.video}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />

              {errorMessage ? (
                <div className={styles.errorOverlay}>
                  <div className={styles.errorIcon}>⚠️</div>
                  <p className={styles.errorText}>{errorMessage}</p>
                  <button
                    type="button"
                    className={styles.errorButton}
                    onClick={handleClose}
                  >
                    {t('common.button.close') || 'Close'}
                  </button>
                </div>
              ) : !isVideoReady ? (
                <div className={styles.loadingOverlay}>
                  <div className={styles.loadingSpinner} />
                  <p className={styles.loadingText}>
                    <p>{t('common_scan.initializing_camera')}</p>
                  </p>
                </div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            className={styles.captureButton}
            onClick={capturePhoto}
            disabled={!isVideoReady || disabled}
          >
            <div className={styles.captureButtonInner} />
          </button>

          <div className={styles.previewWrapper}>{imagePreviewList}</div>
        </div>
      </Modal>
    </div>
  );
};
